import { motion } from 'framer-motion'
import { ShieldCheck, ShieldAlert, HardDrive } from 'lucide-react'
import { SectionLabel } from '../ui/SectionLabel'
import { Button } from '../ui/Button'

const SPECTRA_TEAL = '#0d7a75'

const certCards = [
  {
    icon: ShieldCheck,
    title: 'ENDPOINT SECURITY',
    body: "Your devices and endpoints are certified secure against ransomware. Third-party verified by SPECTRA. Backed by a warranty — if a ransomware attack succeeds, SPECTRA refunds the client directly.",
    badge: '/badges/spectra-endpoint.png',
    alt: 'SPECTRA Certified Endpoint Security',
  },
  {
    icon: ShieldAlert,
    title: 'BEC PROTECTION',
    body: "Business Email Compromise is the #1 cause of cyber-related financial loss. Your email environment is certified protected against fund transfer fraud and data loss. Backed by a warranty.",
    badge: '/badges/spectra-bec.png',
    alt: 'SPECTRA Certified BEC Protection',
  },
  {
    icon: HardDrive,
    title: 'BACKUP',
    body: "Your data backup is certified resilient against any disaster scenario. If recovery fails when needed, SPECTRA refunds the client directly.",
    badge: '/badges/spectra-backup.png',
    alt: 'SPECTRA Certified Backup',
  },
]

export function SpectraSection() {
  return (
    <section style={{ padding: '120px 0', background: 'var(--bg-2)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 48px' }}>
        <SectionLabel>CLIENT SECURITY CERTIFICATION</SectionLabel>

        <h2
          style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(42px, 5.5vw, 68px)',
            textTransform: 'uppercase',
            lineHeight: 1.0,
            color: 'var(--white)',
            marginBottom: 24,
          }}
        >
          {'Your business doesn\'t just get protected. '}
          <span style={{ color: 'var(--accent)' }}>It gets certified. Three times over.</span>
        </h2>

        <p
          style={{
            fontSize: 17,
            color: 'var(--white-dim)',
            maxWidth: 720,
            lineHeight: 1.7,
            marginBottom: 20,
          }}
        >
          Every NextTech Consultants managed IT client with 12 or more employees receives
          three SPECTRA Certificates of Resilience — covering Endpoint Security, BEC Protection,
          and Backup. Each is third-party verified. Each carries a $10,000 warranty. If the
          service fails during a covered event, SPECTRA refunds the client directly — a combined
          $30,000 in coverage, included in your plan.
        </p>

        <p
          style={{
            fontSize: 17,
            color: 'var(--white-dim)',
            lineHeight: 1.75,
            marginBottom: 52,
          }}
        >
          SPECTRA is an independent cybersecurity certification firm. When they certify your
          environment through NextTech Consultants, you receive three separate certificates —
          one for each service — that you can show clients, auditors, and partners. Not a
          promise from your IT provider. Documented, verifiable proof.
        </p>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-[2px]"
          style={{ marginBottom: 3 }}
        >
          {certCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="spectra-card relative overflow-hidden transition-colors duration-250"
              style={{
                background: 'var(--bg-3)',
                padding: '44px 36px',
              }}
            >
              <card.icon
                style={{ color: SPECTRA_TEAL, width: 28, height: 28, marginBottom: 16 }}
              />
              <h3
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: 800,
                  fontSize: 22,
                  textTransform: 'uppercase',
                  color: SPECTRA_TEAL,
                  marginBottom: 14,
                  lineHeight: 1.1,
                }}
              >
                {card.title}
              </h3>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  color: 'var(--white-dim)',
                }}
              >
                {card.body}
              </p>
              <picture>
                <source srcSet={card.badge.replace('.png', '.webp')} type="image/webp" />
                <img
                  src={card.badge}
                  alt={card.alt}
                  width={28}
                  height={28}
                  style={{ height: 28, width: 28, marginTop: 20, display: 'block' }}
                />
              </picture>
            </motion.div>
          ))}
        </div>
        <style dangerouslySetInnerHTML={{ __html: `
          .spectra-card:hover { background: var(--bg-4) !important; }
          .spectra-card::after {
            content: "";
            position: absolute;
            bottom: 0; left: 0;
            width: 100%; height: 2px;
            background: var(--accent);
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 0.3s ease;
          }
          .spectra-card:hover::after { transform: scaleX(1); }
        ` }} />

        <div
          style={{
            marginTop: 3,
            background: 'var(--accent-glow)',
            border: '1px solid var(--accent-line)',
            padding: '20px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 24,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--white-dim)',
              }}
            >
              SPECTRA CARE
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--white-dim)',
                marginTop: 4,
              }}
            >
              per certification, paid directly by SPECTRA
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 32,
                color: 'var(--accent)',
                lineHeight: 1,
              }}
            >
              $10,000 × 3 = $30,000 combined
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--white-dim)',
                marginTop: 6,
                maxWidth: 360,
              }}
            >
              If a covered event occurs and the service fails, SPECTRA pays the refund
              directly to you — not through us.
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 56,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 28,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <picture><source srcSet="/badges/spectra-partner.webp" type="image/webp" /><img src="/badges/spectra-partner.png" alt="SPECTRA Certified Partner" width={52} height={52} style={{ height: 52, width: 52, display: 'block' }} /></picture>
          <picture><source srcSet="/badges/spectra-endpoint.webp" type="image/webp" /><img src="/badges/spectra-endpoint.png" alt="SPECTRA Endpoint Security" width={52} height={52} style={{ height: 52, width: 52, display: 'block' }} /></picture>
          <picture><source srcSet="/badges/spectra-bec.webp" type="image/webp" /><img src="/badges/spectra-bec.png" alt="SPECTRA BEC Protection" width={52} height={52} style={{ height: 52, width: 52, display: 'block' }} /></picture>
          <picture><source srcSet="/badges/spectra-backup.webp" type="image/webp" /><img src="/badges/spectra-backup.png" alt="SPECTRA Backup" width={52} height={52} style={{ height: 52, width: 52, display: 'block' }} /></picture>
        </div>
        <p
          style={{
            fontSize: 13,
            color: 'var(--white-dim)',
            marginTop: 18,
            textAlign: 'center',
            maxWidth: 560,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          NextTech Consultants is a SPECTRA Certified Partner. Qualifying clients receive
          all three service certifications.
        </p>

        <div style={{ marginTop: 48, textAlign: 'center' }}>
          <Button to="/services/managed-it" variant="ghost">
            See What's Included in Your Plan →
          </Button>
        </div>
      </div>
    </section>
  )
}
